import Vue from 'vue'

export default {
  setLoading (state, payload) {
    state.loading = payload
  },
  setContacts (state, payload) {
    state.contacts = payload.map(contact => ({ ...contact, isActive: false }))
  },
  toggleContactsOnInit (state, init) {
    state.contacts = state.contacts.map(contact => {
      const isContactInInit = init.find(initContact => initContact.uuid === contact.uuid)
      const isActive = isContactInInit && isContactInInit.isActive
      if (isActive) {
        return { ...contact, isActive }
      }

      return { ...contact, isActive: false }
    })
  },
  toggleContactSelection (state, payload) {
    state.contacts = state.contacts.map(contact => {
      if (contact.profileId === payload) {
        contact.isActive = !contact.isActive
      } else if (state.contactSingleSelection && contact.isActive) {
        contact.isActive = false
      }

      return contact
    })
  },
  setContactSelectionOption (state, payload) {
    state.contactSingleSelection = payload
  },
  setChannels (state, payload) {
    state.channels = payload

    if (state.currentChannel) {
      state.currentChannelData = payload.find(channel => channel.id === state.currentChannel) || {}
    }

    state.initChatMembers = payload.reduce((acc, channel) => {
      acc[channel.id] = channel.chatMembers
      return acc
    }, {})
  },
  setPrivateChannel(state, payload) {
    state.privateChannel = payload
  },
  addChannel (state, payload) {
    state.channels.push(payload)
  },
  updateChannel (state, payload) {
    state.channels = state.channels.map(channel => {
      if (payload.id === channel.id) {
        return payload
      }

      return channel
    })
    state.currentChannelData = payload
  },
  fetchInitMessages (state, channels) {
    state.messages = channels
  },
  addMessage (state, msg) {
    const { channel } = msg
    if (!state.messages[channel]) {
      Vue.set(state.messages, channel, [msg])
      return
    }

    state.messages[channel].push(msg)
  },
  channelNewUnreadedMessage (state, msg) {
    const { channel } = msg

    if (!state.unreadMessagesObject[channel]) {
      Vue.set(state.unreadMessagesObject, channel, 1)
      return
    }

    state.unreadMessagesObject = {
      ...state.unreadMessagesObject,
      [channel]: state.unreadMessagesObject[channel] + 1
    }
  },
  setAllUnreadMessages (state, unreadMessages) {
    state.unreadMessagesObject = unreadMessages
  },
  setChannelUnreadMessages (state, { channel, currentValue }) {
    state.unreadMessagesObject = {
      ...state.unreadMessagesObject,
      [channel]: currentValue
    }
  },
  setUUID (state, id) {
    state.uuid = id
  },
  setChatIsActive (state, value) {
    state.chatIsActive = value
  },
  setCurrentChannel (state, channelId) {
    state.currentChannelData = state.channels.find(channel => channel.id === channelId)
    state.currentChannel = channelId
  },
  updateMessage (state, newMessage) {
    const { id: newMessageId } = newMessage.message

    if (!newMessageId) {
      return
    }

    state.messages[state.currentChannel] = state.messages[state.currentChannel].map(messageWrapper => {
      const { id } = messageWrapper.message
      return {
        ...messageWrapper,
        message: id === newMessageId
          ? newMessage.message
          : messageWrapper.message
      }
    })
  },
  applyMessageActions (state, { payload, channel = state.currentChannel }) {
    const { data, event } = payload
    state.messages = messagesWithAction(data, state.messages, channel, event)
  },
  addActiveWriter (state, signal) {
    const { channel, publisher } = signal

    if (!state.activeWriters[channel]) {
      Vue.set(state.activeWriters, channel, [])
    }

    if (!state.activeWriters[channel].includes(publisher)) {
      state.activeWriters[channel].push(publisher)
    }
  },
  removeActiveWriter (state, signal) {
    const { channel, publisher } = signal
    state.activeWriters[channel] = state.activeWriters[channel]?.filter(activeUser => activeUser !== publisher)
  },
  setChatMembers (state, { chatMembers, id }) {
    state.channels = state.channels.map(channel => {
      if (channel.id === id) {
        return {
          ...channel,
          chatMembers
        }
      }

      return channel
    })
  },
  addHistoricalMessages (state, { messages, channel, initialized }) {
    const historicalMessages = messages.map(messageObject => {
      let currentMessageObject = messageObject

      if (messageObject.message && messageObject.message.file) {
        currentMessageObject = {
          ...messageObject,
          message: messageObject.message.message,
          file: messageObject.message.file
        }
      }

      const { actions = {} } = messageObject
      Object.keys(actions).map(action => {
        const actionObj = actions[action] || {}
        let lastUpdate = {};

        switch (action) {
          case 'updated':
            Object.entries(actionObj).forEach((updateAction) => {
              // updateAction[1]
              const foundNewerUpdate = updateAction[1].find(newUpdateAction => parseInt(newUpdateAction.actionTimetoken, 10) > parseInt((lastUpdate.actionTimetoken || 0), 10));
              if (foundNewerUpdate) {
                lastUpdate = {
                  ...foundNewerUpdate,
                  value: updateAction[0]
                }
              }
            });
            currentMessageObject.message = {
              ...currentMessageObject.message,
              text: lastUpdate.value,
              updated: true,
              updated_at: lastUpdate.messageTimetoken
            };
            break
          case 'receipt':
            Object.keys(actionObj).map(type => {
              currentMessageObject.message[type] = true
            })
            break
          case 'deleted':
            Object.entries(actionObj).forEach((deletedAction) => {
              const foundNewerUpdate = deletedAction[1].find(newDeletedAction => parseInt(newDeletedAction.actionTimetoken, 10) > parseInt((lastUpdate.actionTimetoken || 0), 10));
              if (foundNewerUpdate) {
                lastUpdate = {
                  ...foundNewerUpdate,
                  value: deletedAction[0]
                }
              }
            });
            currentMessageObject.message = {
              ...currentMessageObject.message,
              text: 'Message removed',
              deleted: true,
              deleted_at: lastUpdate.messageTimetoken
            };
            break
          default: // reactions
            currentMessageObject.message.reactions = Object.keys(actionObj).map(reaction => ({
              type: reaction,
              users: (actionObj[reaction] || []).map(user => ({
                user: user.uuid,
                actionTimetoken: user.actionTimetoken,
                messageTimetoken: messageObject.timetoken
              }))
            }))
        }
      })

      return currentMessageObject
    })

    state.messages[channel] = [
      ...historicalMessages,
      ...initialized ? state.messages[channel] : []
    ]

    if (!initialized) {
      state.initializedChannels = {
        ...state.initializedChannels,
        [channel]: true
      }
    }
  },
  setInitializedChannels (state, data) {
    state.initializedChannels = {
      ...state.initializedChannels,
      ...data
    }
    state.channelWithFullHistory = {
      ...state.channelWithFullHistory,
      ...data
    }
  },
  setChannelWithFullHistory(state, channel) {
    Vue.set(state.channelWithFullHistory, channel, true)
  }
}

const messagesWithAction = (data, messages, channel, event = 'added') => {
  // @todo better support for file
  messages[channel] = (messages[channel] || []).map(messageObject => {
    if (messageObject.message && messageObject.message.file) {
      return {
        ...messageObject,
        message: messageObject.message.message,
        file: messageObject.message.file
      }
    }

    return messageObject
  })

  data.forEach(action => {
    const { messageTimetoken, actionTimetoken, type, value, uuid } = action
    const currentMessages = messages[channel]

    const index = currentMessages
      ? currentMessages.findIndex(message => message.timetoken === messageTimetoken)
      : -1

    // Update messages which replies on that message
    if (type === 'updated' || type === 'deleted') {
      (currentMessages || [])
        .forEach((message, messageIndex) => {
          if (message?.message?.replyMessageTimetoken === messageTimetoken) {
            messages[channel][messageIndex].message.replyMessageUpdatedAt = actionTimetoken

            Vue.set(
              messages[channel][messageIndex],
              'message',
              {...messages[channel][messageIndex].message}
            )
          }
        })
    }


    if (index !== -1) {
      let reactionIndex = -1

      switch(type) {
        case 'updated':
          messages[channel][index].message.updated = true
          messages[channel][index].message.updated_at = actionTimetoken
          messages[channel][index].message.text = value
          break
        case 'receipt':
          messages[channel][index].message[value] = true
          break
        case 'deleted':
          messages[channel][index].message.deleted = true
          messages[channel][index].message.deleted_at = actionTimetoken
          messages[channel][index].message.text = 'Message removed'
          break
        case 'reaction':
          reactionIndex = messages[channel][index].message.reactions
            .findIndex(reaction => reaction.type === value )

          if (event === 'added') {
            if (reactionIndex !== -1) {
              const reaction = messages[channel][index].message.reactions[reactionIndex]
              Vue.set(
                messages[channel][index].message.reactions[reactionIndex],
                'users',
                [...reaction.users, { user: uuid, messageTimetoken, actionTimetoken }]
              )
            } else {
              messages[channel][index].message.reactions = [...messages[channel][index].message.reactions || [], {
                type: value,
                users: [{ user: uuid, messageTimetoken, actionTimetoken }]
              }]
            }
          } else {
            const reactions = messages[channel][index].message.reactions
            const reaction = reactions[reactionIndex]
            const users = reaction.users.filter(user => user.user !== uuid)

            if (users.length === 0) {
              if (reactions.length === 1) {
                Vue.set(
                  messages[channel][index].message,
                  'reactions',
                  []
                )
              } else {
                Vue.set(
                  messages[channel][index].message,
                  'reactions',
                  reactions.filter(react => react.type !== reaction.type)
                )
              }
            } else {
              Vue.set(
                messages[channel][index].message.reactions[reactionIndex],
                'users',
                users
              )
            }
          }
          break
        default:
      }
    } else {
      // console.log('action for unpresent message', action, messages, channel, event)
    }
  })

  return messages
}
